import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { deletePortfolio } from "./index";
import { deleteTagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import { deleteQuery } from "@/widget-components/newsquawk-headlines/api/headline-query";
import type { EditableWatchlist } from "../hooks/useWatchlistCreator";

export interface DeleteWatchlistsResult {
  deleted: EditableWatchlist[];
  failed: EditableWatchlist[];
}

const deleteOne = (item: EditableWatchlist): Promise<unknown> => {
  switch (item.kind) {
    case "watchlist":
      return deletePortfolio(String(item.portfolio.id));
    case "tag":
      return deleteTagsWatchlist(item.watchlist.id);
    case "combined":
      return deleteQuery(item.query.id as number);
  }
};

/**
 * Deletes several of the user's lists at once, whatever store each lives in.
 *
 * Calls the API directly rather than looping over the three per-store delete
 * mutations: each of those invalidates its query on success, so deleting ten
 * lists would start ten refetches of the same data. Here each store is
 * refetched once, when everything has settled.
 *
 * Combined watchlists go first. They refer to other lists by id, so deleting
 * a combination along with the lists inside it should never depend on the
 * server tolerating the references being removed in the wrong order.
 *
 * Never rejects: a partial failure is reported per list, so the caller can
 * say which ones are still there rather than claiming all or nothing.
 */
export function useDeleteWatchlists() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      items: EditableWatchlist[]
    ): Promise<DeleteWatchlistsResult> => {
      const combined = items.filter((i) => i.kind === "combined");
      const rest = items.filter((i) => i.kind !== "combined");

      const result: DeleteWatchlistsResult = { deleted: [], failed: [] };
      for (const batch of [combined, rest]) {
        const settled = await Promise.allSettled(batch.map(deleteOne));
        settled.forEach((outcome, i) => {
          if (outcome.status === "fulfilled") {
            result.deleted.push(batch[i]);
          } else {
            result.failed.push(batch[i]);
            Sentry.captureException(outcome.reason);
          }
        });
      }
      return result;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
      queryClient.invalidateQueries({ queryKey: ["tagsWatchlists"] });
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
    },
  });
}
